// UI metadata never crosses the video generation identity boundary.
export const VIDEO_REFERENCE_LIMIT = 50
export const LEGACY_VIDEO_REFERENCE_LIMIT = 10
export const REFERENCE_FILE_MAX_BYTES = 50 * 1024 * 1024
export const REFERENCE_MEDIA_EXTENSIONS = {
  image: ['png', 'jpg', 'jpeg', 'webp'],
  video: ['mp4', 'webm', 'mov'],
  audio: ['mp3', 'wav', 'm4a', 'ogg']
}
// Video references accept the broader asset-library image registry. Keep this
// separate so the generic image tool keeps its existing upload policy.
export const VIDEO_REFERENCE_MEDIA_EXTENSIONS = {
  image: ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif', 'bmp', 'tif', 'tiff', 'gif'],
  video: REFERENCE_MEDIA_EXTENSIONS.video,
  audio: REFERENCE_MEDIA_EXTENSIONS.audio
}
export function mediaTypeFromApiType(value) {
  if (!['number', 'string'].includes(typeof value)) return ''
  const numericType = Number(value)
  if (numericType === 1) return 'image'
  if (numericType === 2) return 'video'
  if (numericType === 3) return 'audio'
  return ''
}
export function referenceMediaExtension(source = {}) {
  const explicit = String(source.ext || source.extension || '').trim().replace(/^\./, '').toLowerCase()
  if (explicit) return explicit
  const name = String(source.name || source.display_name || source.old_name || '')
  return name.match(/\.([^./\\]+)$/)?.[1]?.toLowerCase() || ''
}
export function referenceMediaType(source = {}) {
  const ext = referenceMediaExtension(source)
  return Object.keys(REFERENCE_MEDIA_EXTENSIONS).find(type => REFERENCE_MEDIA_EXTENSIONS[type].includes(ext)) || ''
}
export function videoReferenceMediaType(source = {}) {
  const declared = source.media_type || source.mediaType
  if (['image', 'video', 'audio'].includes(declared)) return declared
  const apiType = mediaTypeFromApiType(source.type)
  if (apiType) return apiType
  const ext = referenceMediaExtension(source)
  return Object.keys(VIDEO_REFERENCE_MEDIA_EXTENSIONS).find(type => VIDEO_REFERENCE_MEDIA_EXTENSIONS[type].includes(ext)) || ''
}
// Null means unknown, never zero. Keep browser measurements through repeated UI normalization.
export function referenceNumber(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : null
}
export function normalizeReferenceMetadata(source = {}) {
  const data = source.metadata && typeof source.metadata === 'object' ? source.metadata : {}
  const first = (...values) => values.map(referenceNumber).find(value => value !== null) ?? null
  // Asset-library rows may only carry original pixel dimensions in resolution.
  // Keep each width/height pair together; thumbnail geometry is never a source.
  const resolution = typeof source.resolution === 'string'
    ? source.resolution.match(/^\s*([1-9]\d{0,8})\s*[*x×]\s*([1-9]\d{0,8})\s*$/i) : null
  const dimensions = [[data.width, data.height], [source.width, source.height], resolution ? resolution.slice(1) : []]
    .map(pair => pair.map(referenceNumber)).find(pair => pair.length === 2 && pair.every(value => value > 0))
  const seconds = first(source.duration_seconds, source.duration > 0 ? source.duration : null)
  const duration = first(data.duration_ms, source.duration_ms, seconds === null ? null : seconds * 1000)
  const sizeBytes = first(data.size_bytes, source.size_bytes, source.size, source.file_size)
  // AI assets can be registered with a zero placeholder before their object size is probed.
  const deferredSize = sizeBytes === 0 && referenceNumber(source.source_type) === 1 && source.metadata_status !== 'ready'
  return {
    width: dimensions?.[0] ?? first(data.width, source.width), height: dimensions?.[1] ?? first(data.height, source.height),
    size_bytes: deferredSize ? null : sizeBytes,
    duration_ms: duration,
    // Decimal strings are the canonical server representation; compare only at validation time.
    frame_rate: data.frame_rate ?? source.frame_rate ?? null,
    video_codec: data.video_codec ?? source.video_codec ?? null,
    audio_codec: data.audio_codec ?? source.audio_codec ?? null,
    format_name: data.format_name ?? source.format_name ?? null
  }
}
export function normalizeReferenceMedia(source = {}) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null
  const id = Number(source.asset_id ?? source.assetId ?? source.attachment_id ?? source.id)
  const mediaType = videoReferenceMediaType(source)
  if (!Number.isSafeInteger(id) || id < 1 || !mediaType) return null
  const previewUrl = safeReferencePreviewUrl(source.previewUrl || (mediaType === 'image'
    ? source.thumbnail_url || source.preview_url
    : source.preview_url || source.share_url) || '')
  const metadata = normalizeReferenceMetadata(source)
  const result = {
    assetId: id, mediaType, name: String(source.name || source.display_name || source.old_name || ''),
    ext: referenceMediaExtension(source),
    size: metadata.size_bytes, previewUrl,
    duration: metadata.duration_ms === null ? null : metadata.duration_ms / 1000,
    duration_ms: metadata.duration_ms,
    duration_seconds: metadata.duration_ms === null ? null : metadata.duration_ms / 1000,
    metadata,
    metadata_status: ['pending', 'ready', 'failed', 'not_applicable'].includes(source.metadata_status)
      ? source.metadata_status : 'pending',
    metadata_error_code: source.metadata_error_code || null,
    browserMetadataStatus: source.browserMetadataStatus || null,
    browserMetadataErrorCode: source.browserMetadataErrorCode || null
  }
  const expiresAt = Number(source.previewExpiresAt)
  const ttl = Number(mediaType === 'image'
    ? source.thumbnail_expires_in ?? source.preview_expires_in ?? source.expires_in
    : source.preview_expires_in ?? source.expires_in)
  if (previewUrl && expiresAt > 0) result.previewExpiresAt = expiresAt
  else if (previewUrl && Number.isFinite(ttl) && ttl >= 0) result.previewExpiresAt = Date.now() + ttl * 1000
  return result
}
export function safeReferencePreviewUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : ''
}
export function normalizeReferenceMediaList(items = []) {
  const seen = new Set()
  return (Array.isArray(items) ? items : []).map(normalizeReferenceMedia).filter(item => {
    if (!item || seen.has(item.assetId)) return false
    seen.add(item.assetId)
    return true
  })
}
export function validateReferenceFile(file, allowedTypes = ['image', 'video', 'audio']) {
  const type = referenceMediaType(file)
  if (!type || !allowedTypes.includes(type)) return 'unsupported_file'
  if (!file.size || file.size > REFERENCE_FILE_MAX_BYTES) return 'file_too_large'
  // MIME describes the container in some browsers; extension still determines
  // the reference kind. The server must inspect the actual uploaded content.
  const extension = String(file.name || '').split('.').pop().toLowerCase()
  const mime = String(file.type || '').split(';')[0].trim().toLowerCase()
  const audioContainer = (extension === 'm4a' && ['video/mp4', 'application/mp4'].includes(mime)) ||
    (extension === 'ogg' && mime === 'application/ogg')
  if (mime && mime !== 'application/octet-stream' && !mime.startsWith(`${type}/`) && !audioContainer) return 'unsupported_file'
  return ''
}
export function createVideoReferenceDrafts(context = {}) {
  return {
    reference_all: normalizeReferenceMediaList(context.reference_media || context.reference_images || []),
    first_frame: normalizeReferenceMedia(context.first_frame || {}),
    last_frame: normalizeReferenceMedia(context.last_frame || {})
  }
}
export function buildVideoReferenceContext(mode, drafts = {}) {
  const referenceMode = mode === 'first_last_frame' ? mode : 'reference_all'
  const frame = item => {
    const normalized = normalizeReferenceMedia(item || {})
    return normalized?.mediaType === 'image' ? { asset_id: normalized.assetId, media_type: 'image' } : null
  }
  return {
    reference_mode: referenceMode,
    reference_media: referenceMode === 'reference_all'
      ? normalizeReferenceMediaList(drafts.reference_all).map((item, sort) => ({ asset_id: item.assetId, media_type: item.mediaType, sort }))
      : [],
    first_frame: referenceMode === 'first_last_frame' ? frame(drafts.first_frame) : null,
    last_frame: referenceMode === 'first_last_frame' ? frame(drafts.last_frame) : null
  }
}
export function videoReferenceError(mode, drafts = {}, maxCount = VIDEO_REFERENCE_LIMIT) {
  const context = buildVideoReferenceContext(mode, drafts)
  if (context.reference_media.length > Math.min(VIDEO_REFERENCE_LIMIT, maxCount)) return 'too_many'
  return ''
}
export function hasTypedVideoReferenceContext(context) {
  return Boolean(context && typeof context === 'object' && !Array.isArray(context) &&
    ['reference_mode', 'reference_media', 'first_frame', 'last_frame'].some(key => Object.prototype.hasOwnProperty.call(context, key)))
}
// Only known -> different identities invalidate drafts. Initial user-info hydration
// under the same token is not a team switch. Tokens never enter a DOM/session key.
export function didVideoReferenceOwnerChange(previous, current) {
  if (!Array.isArray(previous) || !Array.isArray(current)) return false
  return previous[2] !== current[2] ||
    (Boolean(previous[0]) && previous[0] !== current[0]) ||
    (Boolean(previous[1]) && previous[1] !== current[1])
}
// History v2 stores canonical URLs rather than asset IDs. Keep those identities
// for explicit retry only; preview URLs never become generation references.
function buildVideoUrlSnapshotReferences(context) {
  const referenceMode = context.reference_mode === 'first_last_frame' ? 'first_last_frame' : 'reference_all'
  const identity = (item, imageOnly = false) => {
    if (!item || typeof item !== 'object' || Array.isArray(item) || item.asset_id !== undefined || item.assetId !== undefined) return null
    const url = safeReferencePreviewUrl(item.url)
    const mediaType = item.media_type
    return url && (imageOnly ? mediaType === 'image' : ['image', 'video', 'audio'].includes(mediaType))
      ? { url, media_type: mediaType } : null
  }
  return {
    reference_identity_version: 2,
    reference_mode: referenceMode,
    reference_media: referenceMode === 'reference_all'
      ? (Array.isArray(context.reference_media) ? context.reference_media : []).map(item => identity(item)).filter(Boolean)
        .map((item, sort) => ({ ...item, role: 'reference', sort }))
      : [],
    first_frame: referenceMode === 'first_last_frame' ? identity(context.first_frame, true) : null,
    last_frame: referenceMode === 'first_last_frame' ? identity(context.last_frame, true) : null
  }
}
// Backend question snapshots intentionally exclude provider config and signed URLs.
// Absence of typed keys is meaningful: legacy image_msg references remain authoritative.
export function sanitizeVideoInputSnapshot(context = {}) {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return {}
  const result = hasTypedVideoReferenceContext(context)
    ? context.reference_identity_version === 2
      ? buildVideoUrlSnapshotReferences(context)
      : buildVideoReferenceContext(context.reference_mode, createVideoReferenceDrafts(context))
    : {}
  for (const key of ['selected_video_agent_id', 'duration_seconds', 'resolution', 'aspect_ratio']) {
    if (context[key] !== undefined && context[key] !== null) result[key] = context[key]
  }
  return result
}

// Pure UI snapshots: keep only public preview fields, never private files or generation params.
// The history array is already in display order; repeated frame IDs are intentional.
export function normalizeVideoReferenceMediaPreviews(items) {
  return (Array.isArray(items) ? items : []).flatMap((item, index) => {
    if (!item || !['image', 'video', 'audio'].includes(item.media_type)) return []
    const assetId = Number(item.asset_id)
    const previewUrl = safeReferencePreviewUrl(item.preview_url)
    const thumbnailUrl = safeReferencePreviewUrl(item.thumbnail_url)
    const available = item.available !== false && Boolean(previewUrl || (item.media_type !== 'audio' && thumbnailUrl))
    const result = {
      asset_id: Number.isSafeInteger(assetId) && assetId > 0 ? assetId : null, media_type: item.media_type,
      role: ['first_frame', 'last_frame'].includes(item.role) ? item.role : 'reference',
      sort: Number.isSafeInteger(item.sort) ? item.sort : index,
      name: String(item.name || ''),
      ...((item.metadata || item.duration_ms !== undefined) ? {
        metadata: normalizeReferenceMetadata(item), metadata_status: item.metadata_status || 'pending',
        metadata_error_code: item.metadata_error_code || null,
        browserMetadataStatus: item.browserMetadataStatus || null,
        browserMetadataErrorCode: item.browserMetadataErrorCode || null,
        ext: String(item.ext || '')
      } : {}),
      preview_url: available ? previewUrl : '', thumbnail_url: available ? thumbnailUrl : '', available
    }
    if (Number.isFinite(item.preview_expires_in) && item.preview_expires_in >= 0) result.preview_expires_in = item.preview_expires_in
    return [result]
  })
}
export function buildVideoReferenceMediaPreviews(mode, drafts = {}, display = item => item) {
  const entries = mode === 'first_last_frame'
    ? ['first_frame', 'last_frame'].map(role => ({ item: normalizeReferenceMedia(drafts[role]), role })).filter(entry => entry.item)
    : normalizeReferenceMediaList(drafts.reference_all).map(item => ({ item, role: 'reference' }))
  return normalizeVideoReferenceMediaPreviews(entries.map(({ item, role }, sort) => {
    const shown = display(item) || item
    const expiresAt = Number(shown.previewExpiresAt)
    const url = expiresAt > 0 && expiresAt <= Date.now() ? '' : safeReferencePreviewUrl(shown.previewUrl)
    return {
      asset_id: item.assetId, media_type: item.mediaType, role, sort, name: item.name,
      metadata: item.metadata, metadata_status: item.metadata_status, metadata_error_code: item.metadata_error_code,
      browserMetadataStatus: item.browserMetadataStatus, browserMetadataErrorCode: item.browserMetadataErrorCode, ext: item.ext,
      preview_url: url, thumbnail_url: item.mediaType === 'image' ? url : '', available: Boolean(url),
      ...(Number.isFinite(expiresAt) && expiresAt > 0 ? { preview_expires_in: Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)) } : {})
    }
  }))
}

// Restore composer display metadata separately from the ID/type generation snapshot.
export function applyVideoReferenceMediaPreviews(drafts, mediaPreviews) {
  const previews = normalizeVideoReferenceMediaPreviews(mediaPreviews)
  const attach = (item, role) => {
    if (!item) return null
    const preview = previews.find(row => row.asset_id === item.assetId && row.media_type === item.mediaType && row.role === role)
    return preview ? normalizeReferenceMedia({ ...item, ...preview }) : { ...item }
  }
  return {
    reference_all: drafts.reference_all.map(item => attach(item, 'reference')),
    first_frame: attach(drafts.first_frame, 'first_frame'), last_frame: attach(drafts.last_frame, 'last_frame')
  }
}
